import { HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthJwtTokenStore } from '@ts/shared/authentication/data-access';

/**
 * Applies the logged-in JWT as an `Authorization: Bearer` header on a request
 * that rides an *anonymous* consumer but still wants the backend to see the
 * user — e.g. the cached catalogue search GET, which must stay on the public
 * consumer yet populate `is_authenticated` on the query-log.
 *
 * This is about *using* the token on an outgoing request, not managing it: the
 * authenticated consumers attach (and refresh) the bearer themselves, so they
 * don't need this. Read synchronously off the token store; a stale token reads
 * as anonymous until the next authenticated call refreshes it, which is
 * acceptable for the analytics-only signal it feeds (the token authorizes
 * nothing on the anonymous endpoint).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationHeaderService {
  private tokenStore = inject(AuthJwtTokenStore);

  /**
   * Append `Authorization: Bearer <access>` when the customer is logged in,
   * returning the new (immutable) `HttpHeaders`; a no-op when anonymous.
   */
  applyAuthorizationHeader(headers: HttpHeaders): HttpHeaders {
    const accessToken = this.tokenStore.currentState?.access;
    return accessToken
      ? headers.append('Authorization', `Bearer ${accessToken}`)
      : headers;
  }
}
